import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import SentenceView from './SentenceView';
import ParagraphView from './ParagraphView';
import WordPopup from './WordPopup';
import { useVocab } from '../hooks/useVocab';
import { useProgress } from '../hooks/useProgress';
import { useGloss } from '../hooks/useGloss';
import { useWordTimes } from '../hooks/useWordTimes';
import { stopAll, playWord } from '../utils/audio';
import { cleanWord } from '../utils/wordText';
import { API_BASE } from '../api';

// Same audio-URL formula as SentenceView (filename part encoded; CJK/space safe).
const encPath = (a) => { const i = a.indexOf('/'); return i < 0 ? encodeURIComponent(a) : a.slice(0, i + 1) + encodeURIComponent(a.slice(i + 1)); };
const audioUrlOf = (item) => { const s = item && (item.src || item.audio); return s ? `${API_BASE}/audio/${encPath(s)}` : null; };

export default function Reader({ text, target, onBack, onJumpToReview }) {
  const [viewMode, setViewMode] = useState('sentence');   // phone default
  const [itemIdx, setItemIdx]   = useState(0);
  // Autoplay-on-arrival request, consumed by SentenceView by OBJECT IDENTITY.
  // Every move that should sound stamps a fresh {} here: ‹/›/swipe/keyboard
  // advance, a 目錄 pick, a 跳到故事 landing. Silent moves don't: level switches
  // set it to null, and restoring a saved position / word-stepping across a
  // segment edge leave it untouched. Identity (not index) matters because a 目錄
  // pick lands on the new passage's FIRST unit — itemIdx stays 0, so any
  // index-diff scheme reads it as "no change" and swallows the play.
  const [playReq, setPlayReq]   = useState(null);
  const changeItem = useCallback((i) => { setItemIdx(i); setPlayReq({}); }, []);
  const hasSections = !!(text.sections && text.sections.length);
  // Which passage (= one paragraph) is open. null → show the 目錄 index; a picked
  // passage SCOPES the whole reader to that one paragraph, so ‹/› can't leak into a
  // neighbouring passage and you always know where you are. Texts without sections
  // aren't scoped (activePi 0 = the whole text).
  const [activePi, setActivePi] = useState(hasSections ? null : 0);
  const showIndex = hasSections && activePi == null;
  const visibleSentRef = useRef(null);   // sentence at the top of the 文 viewport (for switching views in place)
  const [popup, setPopup]       = useState(null);
  const { getStatus, setStatus } = useVocab();
  const { getPosition, savePosition, ready: progressReady } = useProgress();
  const { getGloss, setGloss } = useGloss();
  const { getTimes: rawGetTimes } = useWordTimes();   // read-only: timings are baked, no setTimes
  // Namespace timing keys by story code so any pre-existing overrides resolve per story
  // (they all share sentence ids like p01s01).
  const code = text.code || text.id;
  const getTimes = useCallback((sid, wi, fb) => rawGetTimes(`${code}:${sid}`, wi, fb), [rawGetTimes, code]);

  // The paragraph index that contains a sentence/segment id (which passage it lives in).
  const sectionForId = useCallback((sid) =>
    (text.paragraphs || []).findIndex(p =>
      (p.sentences || []).some(s => s.id === sid || (s.segments || []).some(g => g.id === sid))),
  [text.paragraphs]);

  // Every derived list is built from the SCOPED paragraphs — just the open passage
  // (or the whole text when it has no sections). This is what bounds navigation.
  const scopedParas = useMemo(() => {
    if (!hasSections) return text.paragraphs || [];
    if (activePi == null) return [];                       // 目錄 showing
    const p = (text.paragraphs || [])[activePi];
    return p ? [p] : [];
  }, [text.paragraphs, hasSections, activePi]);

  // The section (passage) metadata for the open paragraph — its title/ref for the header.
  const activeSection = useMemo(() => {
    if (activePi == null) return null;
    const p = (text.paragraphs || [])[activePi];
    const fid = p && p.sentences && p.sentences[0] && p.sentences[0].id;
    return (text.sections || []).find(s => s.startId === fid) || null;
  }, [text.paragraphs, text.sections, activePi]);

  // All segments (SRT-level clips): paragraphs → sentences → segments
  const allSegments = useMemo(() =>
    scopedParas.flatMap(p =>
      (p.sentences || []).flatMap(s =>
        (s.segments && s.segments.length > 0)
          ? s.segments.map(g => ({ ...g, sentenceId: s.id, paragraphId: p.id }))
          : [{ ...s, sentenceId: s.id, paragraphId: p.id }]  // no sub-segments: sentence IS the segment
      )
    ),
  [scopedParas]);

  // All sentences
  const allSentences = useMemo(() =>
    scopedParas.flatMap((p, pi) =>
      (p.sentences || []).map(s => ({ ...s, paragraphId: p.id, paragraphIdx: pi }))
    ),
  [scopedParas]);

  // One item per paragraph (段 view) — a span of the source from the first
  // sentence's start to the last sentence's end, with all the paragraph's words.
  const allParagraphs = useMemo(() =>
    scopedParas.map((p, pi) => {
      const ss = p.sentences || [];
      const first = ss[0] || {}, last = ss[ss.length - 1] || {};
      const trans = ss.map(s => s.translation).filter(Boolean).join('');
      return {
        id: `para_${pi}`, paragraphId: p.id, sentenceId: first.id,
        src: first.src, audio: first.audio, origin: first.origin ?? 0, srcDur: first.srcDur,
        start: first.start, end: last.end,
        words: ss.flatMap(s => s.words || []),
        // each sentence is its OWN clip -> the player chains these for whole-paragraph playback
        parts: ss.map(s => ({ sentenceId: s.id, src: s.src, audio: s.audio, origin: s.origin ?? 0,
                              start: s.start, end: s.end, words: s.words || [] })),
        translation: trans || null,
      };
    }),
  [scopedParas]);

  const allItems = viewMode === 'segment' ? allSegments
                 : viewMode === 'paragraph' ? allParagraphs
                 : allSentences;

  // Chapter/scene starts mapped to the CURRENT view's item indices, for the
  // "7 · 13/28"-style position + type-to-jump in the unit views.
  const chapterStarts = useMemo(() => {
    const secs = text.sections || [];
    if (!secs.length) return null;
    const idxOf = (startId) => {
      if (viewMode === 'segment') return allSegments.findIndex(g => g.sentenceId === startId || g.id === startId);
      if (viewMode === 'paragraph') return scopedParas.findIndex(p => (p.sentences || []).some(s => s.id === startId));
      return allSentences.findIndex(s => s.id === startId);
    };
    const starts = secs.map(sec => ({
      n: sec.n,
      label: sec.zhTitle || sec.title || sec.desc || '',
      at: idxOf(sec.startId),
    })).filter(x => x.at >= 0);
    return starts.length ? starts : null;
  }, [text.sections, scopedParas, viewMode, allSegments, allSentences]);

  // 跳到故事 from 複習: land at the word honouring the jump's view + focus.
  //  review-origin → view 'segment', focus → 片 with the word highlighted.
  //  reader-origin → the view I started in, no highlight (back to reading); 文 scrolls
  //  the word's sentence into view (ParagraphView's activeSentId scrollIntoView).
  useEffect(() => {
    if (!target) return;
    // Scope to the passage the target lives in FIRST; the effect re-runs once the
    // scoped lists settle and then resolves the (now passage-local) item index.
    const anchorId = target.segId || target.sentId;
    const pi = sectionForId(anchorId);
    if (pi >= 0 && pi !== activePi) { setActivePi(pi); return; }
    const view = target.view || 'segment';
    setViewMode(view);
    let i = -1;
    if (view === 'segment') i = allSegments.findIndex(g => g.id === target.segId);
    else if (view === 'paragraph') i = 0;                            // one passage = one paragraph
    else i = allSentences.findIndex(s => s.id === target.sentId);    // sentence | story
    if (i >= 0) { setItemIdx(i); setPlayReq({}); }                   // landing sounds (文 has no unit autoplay to consume it)
  }, [target, activePi, allSegments, allSentences, sectionForId]);

  // 跳到故事 → open WORD-VIEW (the popup) on the landed word, matching the phone. The
  // anchored word is marked `.wordview-focus` (in whichever view we land in: 片/句/段/文),
  // so once it's in the DOM we click it — reusing that view's own handleWordClick, which
  // builds the correct popup; the focus-effect below then autoplays it. Once per jump.
  const jumpFocusedRef = useRef(null);
  const skipNextFocusPlayRef = useRef(false);
  useEffect(() => {
    if (!target || !target.focus) { jumpFocusedRef.current = null; return; }
    if (jumpFocusedRef.current === target) return;                     // already focused this jump
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector('.wordview-focus');
      if (!el) return;                                                 // not rendered yet — retry on next settle
      jumpFocusedRef.current = target;
      // In 片/句/段 the unit-arrival autoplay plays the whole unit on landing — so DON'T also
      // word-play here (that races + clips via WORD_TRIM). In 文 there's no unit autoplay, so
      // let the word play. We just open word-view on the word either way.
      if (target.view !== 'story') skipNextFocusPlayRef.current = true;
      el.click();
    });
    return () => cancelAnimationFrame(raf);
  }, [target, itemIdx, viewMode, allItems]);

  // Autoplay on word-FOCUS: whenever word-view (the popup) focuses a new word — by tap,
  // by stepping ←/→, or by a 跳到故事 jump — play it, in every view incl 文. Passive
  // reading (no popup open) stays silent. Keyed on the focused word's identity so gloss/
  // timing edits don't replay it.
  const focusKey = popup ? `${popup.sentenceId}:${popup.wordIdx}` : null;
  useEffect(() => {
    if (!popup || !popup.audioUrl) return;
    if (skipNextFocusPlayRef.current) { skipNextFocusPlayRef.current = false; return; }  // jump-landing: unit autoplay handles it
    const t = getTimes(popup.sentenceId, popup.wordIdx, popup.word);
    playWord(popup.audioUrl, (popup.audioStart ?? 0) + t.start, (popup.audioStart ?? 0) + t.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  // Restore saved position once it's available (progress loads async from the
  // backend, so getPosition may be 0 on first render).
  const restoredRef = useRef(null);
  useEffect(() => { restoredRef.current = null; }, [text.id]);
  useEffect(() => {
    if (!progressReady || restoredRef.current === text.id) return;   // wait for /progress to load
    if (target) { restoredRef.current = text.id; return; }           // a 跳到故事 jump owns the position — don't override it with saved progress
    if (hasSections) { restoredRef.current = text.id; return; }       // sectioned book → always open on 目錄, not mid-scripture
    const saved = getPosition(text.id);
    if (saved > 0) setItemIdx(Math.min(saved, allItems.length - 1));
    restoredRef.current = text.id;                                   // mark restored (even if saved=0)
  }, [text.id, getPosition, allItems.length, progressReady, target]);

  useEffect(() => {
    if (restoredRef.current !== text.id) return;                    // don't save (clobber 0) before restore
    savePosition(text.id, itemIdx);
  }, [itemIdx, text.id, savePosition]);

  // LingQ-style: moving on from a sentence/fragment marks its still-"new" words
  // as "known" (words you explicitly set to "learning" are left alone).
  const prevIdxRef  = useRef(itemIdx);
  const prevViewRef = useRef(viewMode);
  const prevPiRef   = useRef(activePi);
  useEffect(() => {
    // Never auto-mark in whole-story (文) view, when switching views, or when jumping
    // to a different passage (itemIdx resets to 0 in a whole new list).
    if (viewMode === 'story' || prevViewRef.current !== viewMode || prevPiRef.current !== activePi) {
      prevViewRef.current = viewMode;
      prevPiRef.current   = activePi;
      prevIdxRef.current  = itemIdx;
      return;
    }
    const prev = prevIdxRef.current;
    if (prev !== itemIdx) {
      const left = allItems[prev];
      (left?.words || []).forEach(w => {
        const core = cleanWord(w.text);
        if (core && getStatus(text.dialect, core) === 'new') setStatus(text.dialect, core, 'known');
      });
      prevIdxRef.current = itemIdx;
    }
  }, [itemIdx, viewMode, activePi, allItems, getStatus, setStatus, text.dialect]);

  // For popup: find the words array for whatever is currently shown
  const findItemWords = useCallback((sentenceId) => {
    // Check segments first, then sentences
    const seg = allSegments.find(g => g.id === sentenceId);
    if (seg) return seg.words || [];
    const sent = allSentences.find(s => s.id === sentenceId);
    return sent?.words || [];
  }, [allSegments, allSentences]);

  const handleWordClick = useCallback((word, wordIdx, sentenceId, audioUrl, audioStart, e) => {
    setPopup({ word, wordIdx, sentenceId, audioUrl, audioStart,
               allWords: findItemWords(sentenceId) });
  }, [findItemWords]);

  // The word currently being heard (reported by the playing view) — so a view switch lands
  // on the EXACT segment/sentence you're listening to, not the unit's first.
  const playingWordRef = useRef(null);
  const onPlayingWord = useCallback((pw) => { playingWordRef.current = pw; }, []);
  const segIdForWord = useCallback((sentId, wIdx) => {
    const s = allSentences.find((x) => x.id === sentId);
    const segs = (s && s.segments) || [];
    let acc = 0;
    for (const g of segs) { const n = (g.words || []).length; if (wIdx != null && wIdx < acc + n) return g.id; acc += n; }
    return segs[0] ? segs[0].id : null;
  }, [allSentences]);

  // Switch view level while keeping your place: anchor on the sentence (and word) you're at,
  // and map it into the target level — the exact segment for 片, the sentence, or the paragraph.
  const goToView = (mode, sentId, wIdx) => {
    stopAll(); setPopup(null); setViewMode(mode);
    setPlayReq(null);                       // level switches stay silent
    let newIdx = 0;
    if (sentId != null) {
      if (mode === 'paragraph') {
        const f = scopedParas.findIndex(p => (p.sentences || []).some(s => s.id === sentId));
        if (f >= 0) newIdx = f;
      } else if (mode === 'segment') {
        const segId = segIdForWord(sentId, wIdx);
        let found = segId ? allSegments.findIndex(g => g.id === segId) : -1;
        if (found < 0) found = allSegments.findIndex(it => (it.sentenceId || it.id) === sentId);
        if (found >= 0) newIdx = found;
      } else { // sentence | story — both index allSentences
        const found = allSentences.findIndex(it => (it.sentenceId || it.id) === sentId);
        if (found >= 0) newIdx = found;
      }
    }
    setItemIdx(newIdx);
  };

  const handleViewChange = (mode) => {
    const pw = playingWordRef.current;
    let sentId = null, wIdx = null;
    if (pw) { sentId = pw.sentId; wIdx = pw.wIdx; }                                          // playing → land where you're hearing
    else if (viewMode === 'story' && visibleSentRef.current) sentId = visibleSentRef.current; // 文 reading → the sentence at top
    else { const cur = allItems[itemIdx]; sentId = cur ? (cur.sentenceId || cur.id) : null; }
    goToView(mode, sentId, wIdx);
  };

  return (
    <div className="reader">
      <div className="reader-top">
        <div className="rd-header">
          <button className="rd-back" onClick={() => { stopAll(); onBack(); }}>‹</button>
          {/* In a passage, name the passage (so you always know where you are); on the
              index, name the panel. */}
          <h2 className="rd-title">{showIndex ? text.title : (activeSection ? (activeSection.zhTitle || activeSection.title) : text.title)}</h2>
          {hasSections && !showIndex
            ? <button className="rd-toc" onClick={() => { stopAll(); setActivePi(null); }}>目錄</button>
            : <span className="rd-right" />}
        </div>
        {/* No 段 here. In this book a chapter IS one paragraph (one pericope, built as a
            single paragraph of verses), so 段 rendered exactly the same span as 文 — a
            button that did nothing. The machinery below still supports 'paragraph'; if a
            chapter ever gains real internal paragraphs, restore the button. */}
        {/* The reading-level bar is meaningless on the 目錄 landing — hide it there.
            Short → medium → long: 句 (clause) · 節 (verse) · 文 (whole passage). */}
        {!showIndex && (
          <div className="modes">
            <button className={`mode-btn${viewMode === 'segment'   ? ' active' : ''}`} onClick={() => handleViewChange('segment')}>句</button>
            <button className={`mode-btn${viewMode === 'sentence'  ? ' active' : ''}`} onClick={() => handleViewChange('sentence')}>節</button>
            <button className={`mode-btn${viewMode === 'story'     ? ' active' : ''}`} onClick={() => handleViewChange('story')}>文</button>
          </div>
        )}
      </div>

      <div className="reader-body" onClick={(e) => { if (e.target.closest && e.target.closest('.wave')) return; setPopup(null); }}>
        {showIndex ? (
          <PassageIndex
            titleTranslation={text.titleTranslation}
            sections={text.sections || []}
            onPick={(startId) => {
              stopAll(); setPopup(null);
              const pi = sectionForId(startId);
              if (pi >= 0) setActivePi(pi);
              changeItem(0);                     // first verse/clause of the passage — and PLAY it
            }} />
        ) : viewMode === 'story' ? (
          <ParagraphView
            text={{ ...text, paragraphs: scopedParas, sections: activeSection ? [activeSection] : [] }}
            activeSentId={allSentences[itemIdx]?.id}
            onVisibleSentence={(sid) => { visibleSentRef.current = sid; }}
            onPlayingWord={onPlayingWord}
            onWordClick={handleWordClick}
            getWordStatus={(w) => getStatus(text.dialect, w)}
            focusSentId={target && target.focus && target.view === 'story' ? target.anchorId : null}
            focusWIdx={target && target.focus && target.view === 'story' ? target.anchorWIdx : -1}
          />
        ) : (
          <SentenceView
            code={code}
            paraMode={viewMode === 'paragraph'}
            returnWordIdx={(target && target.focus && allItems[itemIdx] && allItems[itemIdx].id === target.anchorId) ? target.anchorWIdx : -1}
            allSentences={allItems}
            sentenceIdx={itemIdx}
            onSentenceChange={changeItem}
            playReq={playReq}
            onPlayingWord={onPlayingWord}
            onWordClick={handleWordClick}
            getWordStatus={(w) => getStatus(text.dialect, w)}
            getTimes={getTimes}
            popupOpen={!!popup}
            selWordIdx={popup && allItems[itemIdx] && popup.sentenceId === allItems[itemIdx].id
              ? popup.wordIdx : -1}
            chapters={chapterStarts}
            unitName={viewMode === 'segment' ? '句' : viewMode === 'paragraph' ? '段' : '節'}
            dialect={text.dialect}
          />
        )}
      </div>

      {popup && (
        <div className="word-sheet-anchor">
          <WordPopup
            word={popup.word}
            dialect={text.dialect}
            sentenceId={popup.sentenceId}
            wordIdx={popup.wordIdx}
            allWords={popup.allWords}
            audioUrl={popup.audioUrl}
            audioStart={popup.audioStart}
            status={getStatus(text.dialect, popup.word.text)}
            onStatusChange={(s) => {
              setStatus(text.dialect, popup.word.text, s,
                { code, itemId: popup.sentenceId, wi: popup.wordIdx });
            }}
            onClose={(jumpWord, jumpIdx) => {
              // '__cross__': stepping past a segment edge → adjacent segment (jumpIdx = ±1).
              // Advance the reader to that segment and open word-view on its first/last word.
              if (jumpWord === '__cross__') {
                const cur = allItems.findIndex(it => it.id === popup.sentenceId);
                const ni = cur + jumpIdx;
                if (cur < 0 || ni < 0 || ni >= allItems.length) return;   // at the very ends — keep the popup
                const it = allItems[ni];
                const ws = it.words || [];
                if (!ws.length) return;
                const wi = jumpIdx > 0 ? 0 : ws.length - 1;
                setItemIdx(ni);
                setPopup({ word: { ...ws[wi], text: cleanWord(ws[wi].text) }, wordIdx: wi,
                  sentenceId: it.id, audioUrl: audioUrlOf(it), audioStart: it.origin ?? 0,
                  allWords: ws });
                return;
              }
              // jumpWord must be a real word token (has timing); ignore stray events
              if (jumpWord && jumpWord.start !== undefined) {
                setPopup(prev => ({ ...prev,
                  word: { ...jumpWord, text: cleanWord(jumpWord.text) },
                  wordIdx: jumpIdx,
                  allWords: findItemWords(prev.sentenceId) }));
              } else {
                setPopup(null);
              }
            }}
            onJumpToReview={onJumpToReview ? () => onJumpToReview(cleanWord(popup.word.text), text.dialect, viewMode, popup.sentenceId, popup.wordIdx) : undefined}
            getGloss={getGloss}
            setGloss={setGloss}
            getTimes={getTimes}
          />
        </div>
      )}
    </div>
  );
}

// The passage index (目錄) shown on opening a Bible panel: its chapters grouped by genre
// (故事 · 講論 · 書信 · 詩歌), each a tappable row that jumps to that passage's first verse.
// The `sections` array is built by build_bible.py (n, title, zhTitle, ref, genre, startId).
const GENRE_ORDER = ['故事', '講論', '書信', '詩歌'];
function PassageIndex({ titleTranslation, sections, onPick }) {
  const groups = {};
  for (const s of sections) (groups[s.genre || '其他'] ||= []).push(s);
  const genres = [...GENRE_ORDER.filter(g => groups[g]),
                  ...Object.keys(groups).filter(g => !GENRE_ORDER.includes(g))];
  return (
    <div className="pidx">
      {/* The panel title is already in the header — here just say what this screen is. */}
      <div className="pidx-head">
        <p className="pidx-sub">{titleTranslation ? `${titleTranslation} · 選讀目錄` : '選讀目錄'}</p>
      </div>
      {genres.map(g => (
        <div key={g} className="pidx-group">
          <div className="pidx-genre">{g}</div>
          {groups[g].map(s => (
            <button key={s.startId} className="pidx-row" onClick={() => onPick(s.startId)}>
              <span className="pidx-zh">{s.zhTitle || s.title}</span>
              <span className="pidx-ref">{s.ref}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
